/**
 * Switch — Toggle for instant-effect on/off settings.
 *
 * Switch Component Guidelines
 * - Use Switch for settings that take effect immediately (feature flags, notifications,
 *   dark mode). For choices that require a form submission to save, use Checkbox instead.
 * - Use `size="sm"` (18px) in dense settings lists or table rows; `size="base"` (24px)
 *   for prominent standalone toggles.
 * - Always pair with `<Field>` + `<FieldLabel>` for accessibility — a standalone Switch
 *   has no visible or programmatic label.
 * - Set `invalid` when the toggle is in a conflicting or error state. Pair with a
 *   sibling `<FieldError>` to explain the issue.
 * @see https://base-ui.com/react/components/switch Base UI Switch documentation
 * @see https://ui.shadcn.com/docs/components/switch shadcn/ui Switch documentation
 */
import type { LayoutClassName } from "@replohq/lattice/types/layoutClassName";

import React from "react";

import { Switch as SwitchPrimitive } from "@base-ui/react/switch";
import { cn } from "@replohq/lattice/utils/cn";

interface SwitchProps<ClassName extends string = string>
  extends Omit<SwitchPrimitive.Root.Props, "className"> {
  size?: "sm" | "base";
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
  invalid?: boolean;
}

function SwitchInner<ClassName extends string>(
  {
    className,
    UNSAFE_className,
    size = "base",
    invalid,
    onClick,
    ...props
  }: SwitchProps<ClassName>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  return (
    <SwitchPrimitive.Root
      ref={ref}
      data-slot="switch"
      data-size={size}
      aria-invalid={invalid}
      className={cn(
        "data-checked:bg-primary data-unchecked:bg-surface focus-visible:border-ring focus-visible:ring-ring/50 shrink-0 rounded-full border border-transparent aria-invalid:border-rose-border aria-invalid:ring-[3px] aria-invalid:ring-rose-border focus-visible:ring-[3px] data-[size=base]:h-[24px] data-[size=base]:w-[42.667px] data-[size=sm]:h-[18px] data-[size=sm]:w-[32px] peer group/switch relative inline-flex items-center transition-all outline-none after:absolute after:-inset-x-3 after:-inset-y-2 cursor-pointer data-disabled:cursor-not-allowed data-disabled:opacity-50",
        className,
        UNSAFE_className,
      )}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className="bg-sheet rounded-full shadow-lg group-data-[size=base]/switch:size-[20px] group-data-[size=sm]/switch:size-[15px] group-data-[size=base]/switch:data-checked:translate-x-[calc(100%-1px)] group-data-[size=sm]/switch:data-checked:translate-x-[calc(100%-1px)] group-data-[size=base]/switch:data-unchecked:translate-x-px group-data-[size=sm]/switch:data-unchecked:translate-x-px pointer-events-none block ring-0 transition-transform"
      />
    </SwitchPrimitive.Root>
  );
}

// NOTE (Ben, 01/22/2026): Type cast to preserve generic type parameter for className prop
// See: https://fettblog.eu/typescript-react-generic-forward-refs/
// Legacy cast - may or may not be necessary
// eslint-disable-next-line replo/no-as-cast
const Switch = React.forwardRef(SwitchInner) as <ClassName extends string>(
  props: SwitchProps<ClassName> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactNode;

export { Switch };
